import { useCallback, useEffect, useMemo, useRef } from 'react'

import { type ContactCandidate } from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import {
	CONTACT_DIRECTORY_SECTION,
	type ContactDirectoryEntry,
	type ContactInput,
	type UpsertOptions,
	directoriesEqual,
	mapEntriesToCandidates,
	normalizeDirectoryEntries,
	normalizeContactInput,
	removeDirectoryEntry,
	seedContactsFromSections,
	upsertDirectoryEntry,
} from '#app/routes/mocra+/_form+/contact-directory.ts'
import {
	useIntakeDraftState,
	useIntakeDraftStore,
} from '#app/routes/mocra+/_form+/intake-context.tsx'

declare global {
	interface Window {
		__mocraContactDirectoryLastError?: unknown
	}
}

type DebugLogger = {
	info: (...args: unknown[]) => void
	warn: (...args: unknown[]) => void
	error: (...args: unknown[]) => void
	enabled: boolean
}

const DEBUG_NAMESPACE = '[MoCRA contact directory]'
const ERROR_STORAGE_KEY = 'mocra-contact-directory-last-error'

function noop() {}

function isContactDirectoryDebugEnabled(): boolean {
	if (typeof window === 'undefined') return false
	try {
		const searchParams = new URLSearchParams(window.location.search)
		const queryValue = searchParams.get('debugContacts')
		if (queryValue === 'true') return true
		if (queryValue === 'false') return false
		const storageValue = window.localStorage.getItem(
			'mocra-contact-directory-debug',
		)
		return storageValue === 'true'
	} catch {
		return false
	}
}

function createDebugLogger(): DebugLogger {
	const enabled = isContactDirectoryDebugEnabled()
	if (!enabled) {
		return { enabled, info: noop, warn: noop, error: noop }
	}
	return {
		enabled,
		info: (...args: unknown[]) => console.info(DEBUG_NAMESPACE, ...args),
		warn: (...args: unknown[]) => console.warn(DEBUG_NAMESPACE, ...args),
		error: (...args: unknown[]) => console.error(DEBUG_NAMESPACE, ...args),
	}
}

function summarizeContactInput(input: ContactInput) {
	try {
		const candidate = normalizeContactInput(input)
		if (!candidate) return { normalized: null }
		return {
			normalized: {
				id: candidate.id,
				email: candidate.email,
				name: candidate.name,
				title: candidate.title,
				phone: candidate.phone,
				source: candidate.source,
				updatedAt: candidate.updatedAt,
			},
		}
	} catch (error) {
		return {
			normalized: null,
			normalizationError: normalizeUnknownError(error),
		}
	}
}

function summarizeRawEntries(raw: unknown) {
	if (Array.isArray(raw)) {
		return {
			type: 'array',
			length: raw.length,
			sample: raw.slice(0, 3).map(summarizeEntryPreview),
		}
	}
	if (raw && typeof raw === 'object') {
		const keys = Object.keys(raw as Record<string, unknown>).slice(0, 8)
		return {
			type: 'object',
			keys,
		}
	}
	return {
		type: typeof raw,
		value: raw,
	}
}

function summarizeEntryPreview(entry: unknown) {
	if (!entry || typeof entry !== 'object') {
		return {
			type: typeof entry,
			value: entry,
		}
	}
	const record = entry as Record<string, unknown>
	const summary: Record<string, unknown> = {
		type: 'object',
		keys: Object.keys(record).slice(0, 8),
	}
	const fields = ['id', 'email', 'name', 'title', 'phone', 'source'] as const
	for (const field of fields) {
		const value = record[field]
		if (typeof value === 'string') {
			summary[field] = value.slice(0, 120)
		}
	}
	return summary
}

type ErrorReportDetails = Record<string, unknown> | undefined

type NormalizedError = {
	message: string
	stack?: string
}

function normalizeUnknownError(error: unknown): NormalizedError {
	if (error instanceof Error) {
		return {
			message: error.message,
			stack: error.stack ?? undefined,
		}
	}
	if (typeof error === 'string') {
		return { message: error }
	}
	return { message: `Non-error thrown: ${String(error)}` }
}

function persistErrorReport(payload: {
	context: string
	timestamp: number
	message: string
	stack?: string
	details: ErrorReportDetails
}) {
	if (typeof window === 'undefined') return
	try {
		if (typeof window.sessionStorage !== 'undefined') {
			window.sessionStorage.setItem(ERROR_STORAGE_KEY, JSON.stringify(payload))
		}
	} catch {
		// Ignore storage failures (private mode, quota, etc.)
	}
	try {
		window.__mocraContactDirectoryLastError = payload
	} catch {
		// Ignore assignment failures
	}
}

function reportContactDirectoryError(
	context: string,
	error: unknown,
	details: ErrorReportDetails = undefined,
) {
	const normalized = normalizeUnknownError(error)
	const payload = {
		context,
		timestamp: Date.now(),
		message: normalized.message,
		stack: normalized.stack,
		details,
	}
	persistErrorReport(payload)
	console.error(`${DEBUG_NAMESPACE} ${context} failed`, {
		error,
		details,
		payload,
	})
}

export function useContactDirectory() {
	const store = useIntakeDraftStore()
	const state = useIntakeDraftState()
	const debug = useMemo(createDebugLogger, [])

	const rawEntries = state.sections[CONTACT_DIRECTORY_SECTION]
	const storedEntries = Array.isArray(rawEntries)
		? (rawEntries as ContactDirectoryEntry[])
		: null
	const entries = useMemo(() => {
		try {
			return normalizeDirectoryEntries(rawEntries)
		} catch (error) {
			reportContactDirectoryError('normalize', error, {
				rawSummary: summarizeRawEntries(rawEntries),
			})
			return []
		}
	}, [rawEntries])

	useEffect(() => {
		if (typeof window === 'undefined') return
		try {
			const snapshot = window.sessionStorage?.getItem(ERROR_STORAGE_KEY)
			if (!snapshot) return
			const parsed = JSON.parse(snapshot) as Record<string, unknown>
			console.info(`${DEBUG_NAMESPACE} previous failure snapshot`, parsed)
		} catch {
			// Ignore snapshot parsing errors
		}
	}, [])

	useEffect(() => {
		if (!debug.enabled) return
		debug.info('Hydrated contact directory state', {
			rawType: Array.isArray(rawEntries) ? 'array' : typeof rawEntries,
			rawLength: Array.isArray(rawEntries) ? rawEntries.length : undefined,
			storedLength: storedEntries?.length ?? null,
			normalizedLength: entries.length,
		})
	}, [debug, entries.length, rawEntries, storedEntries])

	const seededRef = useRef(false)

	// Ensure the store always holds sanitized entries for the section.
	useEffect(() => {
		if (!directoriesEqual(storedEntries, entries)) {
			try {
				store.updateSection(CONTACT_DIRECTORY_SECTION, entries)
				if (debug.enabled) {
					debug.info('Synchronized sanitized contact directory entries', {
						previousLength: storedEntries?.length ?? 0,
						nextLength: entries.length,
					})
				}
			} catch (error) {
				reportContactDirectoryError('synchronize', error, {
					previousLength: storedEntries?.length ?? 0,
					nextLength: entries.length,
				})
			}
		} else if (debug.enabled) {
			debug.info('Contact directory already synchronized')
		}
	}, [debug, entries, storedEntries, store])

	// Seed the directory once using existing draft sections if empty.
	useEffect(() => {
		if (seededRef.current) return
		if (entries.length > 0) {
			seededRef.current = true
			if (debug.enabled) {
				debug.info('Contact directory already seeded', {
					entryCount: entries.length,
				})
			}
			return
		}
		let seeds: ContactDirectoryEntry[] = []
		let snapshotSections: Record<string, unknown> | null = null
		try {
			const snapshot = store.getState()
			snapshotSections = snapshot.sections
			seeds = seedContactsFromSections(snapshot.sections)
		} catch (error) {
			seededRef.current = true
			reportContactDirectoryError('seed-from-sections', error, {
				availableSections: snapshotSections
					? Object.keys(snapshotSections)
					: null,
			})
			return
		}
		if (seeds.length > 0) {
			seededRef.current = true
			try {
				store.updateSection(CONTACT_DIRECTORY_SECTION, seeds)
				if (debug.enabled) {
					debug.info('Seeded contact directory from existing sections', {
						seedCount: seeds.length,
					})
				}
			} catch (error) {
				reportContactDirectoryError('seed', error, {
					seedCount: seeds.length,
				})
			}
		} else {
			seededRef.current = true
			if (debug.enabled) {
				debug.info('No contacts available to seed the directory')
			}
		}
	}, [debug, entries.length, store])

	const upsertContact = useCallback(
		(
			input: ContactInput,
			options: UpsertOptions = {},
		): ContactDirectoryEntry | null => {
			let result: ContactDirectoryEntry | null = null
			try {
				store.updateSection(CONTACT_DIRECTORY_SECTION, (previous: unknown) => {
					const current = Array.isArray(previous)
						? (previous as ContactDirectoryEntry[])
						: []
					const { entries: nextEntries, entry } = upsertDirectoryEntry(
						current,
						input,
						options,
					)
					if (entry) {
						result = entry
					}
					if (debug.enabled) {
						debug.info('Upserted contact directory entry', {
							beforeLength: current.length,
							afterLength: nextEntries.length,
							options,
							entry: entry
								? {
										id: entry.id,
										email: entry.email,
										source: entry.source,
										name: entry.name,
									}
								: null,
							inputSummary: summarizeContactInput(input),
						})
					}
					return nextEntries
				})
			} catch (error) {
				reportContactDirectoryError('upsert', error, {
					inputSummary: summarizeContactInput(input),
					options,
				})
			}
			return result
		},
		[debug, store],
	)

	const mergeContacts = useCallback(
		(inputs: readonly ContactInput[], options: UpsertOptions = {}) => {
			if (inputs.length === 0) return
			try {
				store.updateSection(CONTACT_DIRECTORY_SECTION, (previous: unknown) => {
					let next = Array.isArray(previous)
						? (previous as ContactDirectoryEntry[])
						: []
					const beforeLength = next.length
					for (const input of inputs) {
						const { entries: mergedEntries } = upsertDirectoryEntry(
							next,
							input,
							options,
						)
						next = mergedEntries
					}
					if (debug.enabled) {
						debug.info('Merged contact directory entries', {
							inputCount: inputs.length,
							options,
							beforeLength,
							afterLength: next.length,
						})
					}
					return next
				})
			} catch (error) {
				reportContactDirectoryError('merge', error, {
					inputCount: inputs.length,
					options,
				})
			}
		},
		[debug, store],
	)

	const removeContact = useCallback(
		(id: string) => {
			try {
				store.updateSection(CONTACT_DIRECTORY_SECTION, (previous: unknown) => {
					const current = Array.isArray(previous)
						? (previous as ContactDirectoryEntry[])
						: []
					const next = removeDirectoryEntry(current, id)
					if (debug.enabled) {
						debug.info('Removed contact directory entry', {
							id,
							beforeLength: current.length,
							afterLength: next.length,
							removed: next.length !== current.length,
						})
					}
					if (next.length === current.length) return current
					return next
				})
			} catch (error) {
				reportContactDirectoryError('remove', error, { id })
			}
		},
		[debug, store],
	)

	const candidates = useMemo<ContactCandidate[]>(() => {
		try {
			return mapEntriesToCandidates(entries)
		} catch (error) {
			reportContactDirectoryError('map-candidates', error, {
				entryCount: entries.length,
			})
			return []
		}
	}, [entries])

	return {
		contacts: entries,
		candidates,
		upsertContact,
		mergeContacts,
		removeContact,
	}
}
